/**
 * src/features/admin/ui/TimeToExportCards.tsx
 *
 * Two stat cards: median (p50) and p75 time to first export (Phase 23 / METRIC-03).
 * Mirrors MrrGrid layout: grid-cols-1 sm:grid-cols-2.
 * Values formatted as "Xh Ym" or "Xm"; null → "—".
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/ui/card';

// ---------------------------------------------------------------------------
// File-local helper (not exported — used only here)
// ---------------------------------------------------------------------------

function formatMinutes(n: number | null): string {
  if (n === null) return '—';
  const rounded = Math.round(n);           // round once; eliminates "60m" and "Xh 60m" at boundaries
  if (rounded < 60) return `${rounded}m`;
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;           // always 0–59 since rounded is an integer
  return `${hours}h ${minutes}m`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TimeToExportCardsProps {
  p50: number | null;
  p75: number | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TimeToExportCards({
  p50,
  p75,
}: TimeToExportCardsProps): React.ReactElement {
  const { t } = useTranslation();

  const cards: Array<{ key: string; label: string; value: number | null }> = [
    {
      key: 'p50',
      label: t('admin.activation.p50_label', 'Median Time to Export'),
      value: p50,
    },
    {
      key: 'p75',
      label: t('admin.activation.p75_label', 'P75 Time to Export'),
      value: p75,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
      {cards.map(({ key, label, value }) => (
        <Card key={key}>
          <CardContent className="p-6 flex flex-col gap-2">
            <dt className="text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">
              {label}
            </dt>
            <dd
              className={
                value === null
                  ? 'text-subhead font-bold font-mono tabular-nums text-slate-400 dark:text-slate-500'
                  : 'text-subhead font-bold font-mono tabular-nums text-slate-900 dark:text-slate-50'
              }
            >
              {formatMinutes(value)}
            </dd>
            <p className="text-caption text-slate-400 dark:text-slate-500">
              {t('admin.activation.time_note', 'From trial start to first export')}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
